export const CHAT_WEBHOOK_URL = 'https://n8n.operines.me/webhook/operines/view'

export interface ChatUser {
  chatId: string
  name: string
  avatar?: string | null
  lastMessage?: string
  lastMessageAt?: string
  unreadCount?: number
}

export interface ChatMessage {
  id: string
  fromMe: boolean
  text: string
  type: string
  sentAt: string
}

export interface ChatResponse<T> {
  ok: boolean
  action: string
  data?: T
  pagination?: {
    limit: number
    offset?: number
    before?: string | null
    hasMore: boolean
  }
  chatId?: string
  messageId?: string
  clientMessageId?: string
  sentAt?: string
}

const makeRequest = async (body: Record<string, any>): Promise<any> => {
  try {
    const response = await fetch(CHAT_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    const contentType = response.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      return await response.json()
    }

    // If not JSON, try to parse anyway
    const text = await response.text()
    try {
      return JSON.parse(text)
    } catch {
      console.error('Non-JSON response:', text)
      return { ok: false, error: 'Invalid response format', raw: text }
    }
  } catch (error: any) {
    console.error('Chat API error:', error)

    // Try no-cors fallback
    if (
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('CORS')
    ) {
      try {
        await fetch(CHAT_WEBHOOK_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        return { ok: true, data: [], _noCors: true }
      } catch {
        // silent
      }
    }

    return { ok: false, error: error.message }
  }
}

// Extract the root object from various response shapes
function extractRoot(result: any): any {
  // API returns Array<{ ok, action, data, total }> — unwrap first element
  if (Array.isArray(result) && result.length > 0 && result[0]?.action) {
    return result[0]
  }
  // Already an object
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    return result
  }
  return { ok: false, data: [] }
}

export const chatApi = {
  listChats: async (
    instanceName: string,
    limit = 50,
    offset = 0,
  ): Promise<ChatResponse<ChatUser[]>> => {
    const result = await makeRequest({
      action: 'list_chats',
      instanceName,
      limit,
      offset,
    })

    const root = extractRoot(result)

    // Extract chats array from root.data
    let chatsArray: ChatUser[] = []
    if (Array.isArray(root.data)) {
      chatsArray = root.data
    } else if (root.data?.data && Array.isArray(root.data.data)) {
      chatsArray = root.data.data
    }

    // Filter out invalid chatIds like "0@s.whatsapp.net"
    chatsArray = chatsArray.filter(
      (c) => c.chatId && c.chatId !== '0@s.whatsapp.net',
    )

    return {
      ok: root.ok !== false,
      action: 'list_chats',
      data: chatsArray,
      pagination: root.pagination,
    }
  },

  listMessages: async (
    instanceName: string,
    chatId: string,
    limit = 50,
    before: string | null = null,
  ): Promise<ChatResponse<ChatMessage[]>> => {
    const result = await makeRequest({
      action: 'list_messages',
      instanceName,
      chatId,
      limit,
      before,
    })

    const root = extractRoot(result)

    let messagesArray: ChatMessage[] = []
    if (Array.isArray(root.data)) {
      messagesArray = root.data
    } else if (root.data?.data && Array.isArray(root.data.data)) {
      messagesArray = root.data.data
    }

    return {
      ok: root.ok !== false,
      action: 'list_messages',
      data: messagesArray,
      chatId: root.chatId || chatId,
      pagination: root.pagination,
    }
  },

  sendText: async (
    instanceName: string,
    chatId: string,
    text: string,
  ): Promise<ChatResponse<any>> => {
    const clientMessageId = `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
    const result = await makeRequest({
      action: 'send_text',
      instanceName,
      chatId,
      text,
      clientMessageId,
    })
    return result || { ok: false, action: 'send_text' }
  },
}
